import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Users,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  KeyRound,
  Loader2,
  Copy,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { useSortableData } from "@/hooks/useSortableData";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { formatDateTime } from "@/lib/dateFormat";
import { ftpApi } from "@/services/api/ftp";
import type {
  FtpUser,
  CreateUserRequest,
  UpdateUserRequest,
} from "@/services/api/ftp";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/ftp/users")({
  component: FtpUsersPage,
});

function FtpUsersPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canCreate = can("create", "ftp");
  const canEdit = can("edit", "ftp");
  const canDelete = can("delete", "ftp");

  const [searchTerm, setSearchTerm] = useState("");

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<FtpUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<FtpUser | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<FtpUser | null>(
    null,
  );
  const [newPassword, setNewPassword] = useState<string | null>(null);

  // Form state
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formStatus, setFormStatus] = useState(1);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  // Server-side paginated data
  const fetchUsers = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<FtpUser>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };

      const params = new URLSearchParams();
      params.set("limit", String(pageSize));
      params.set("offset", String((page - 1) * pageSize));
      if (searchTerm) params.set("search", searchTerm);

      const response = await ftpApi.listUsers(token, params.toString());
      return {
        data: response.users || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, searchTerm],
  );

  const {
    data: users,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    refresh,
  } = usePaginatedData<FtpUser>({
    fetchFn: fetchUsers,
    initialPageSize: 25,
    dependencies: [token, searchTerm],
  });

  // Client-side sorting on current page data
  const { sortedData, requestSort, getSortDirection } = useSortableData(users, {
    initialSort: { key: "username", direction: "asc" },
  });

  function openCreateDialog() {
    setFormUsername("");
    setFormPassword("");
    setFormDescription("");
    setShowCreateDialog(true);
  }

  function openEditDialog(user: FtpUser) {
    setFormDescription(user.description || "");
    setFormStatus(user.status);
    setEditingUser(user);
  }

  function closeDialogs() {
    setShowCreateDialog(false);
    setEditingUser(null);
    setFormUsername("");
    setFormPassword("");
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !formUsername) return;
    // FT31: mirror the backend Pydantic constraints (username 3-100, password
    // >=8 when set) so short values show inline, not as a 422 toast after the
    // round-trip. The inputs also carry minLength/maxLength for native hints.
    if (formUsername.length < 3 || formUsername.length > 100) {
      toast.error("Username must be between 3 and 100 characters.");
      return;
    }
    if (formPassword && formPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setFormSubmitting(true);
    try {
      const data: CreateUserRequest = {
        username: formUsername,
        description: formDescription || undefined,
      };
      if (formPassword) data.password = formPassword;
      const result = await ftpApi.createUser(token, data);
      toast.success(`User "${result.username}" created`);
      if (result.initial_password) {
        setNewPassword(result.initial_password);
      }
      closeDialogs();
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setFormSubmitting(false);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !editingUser) return;
    setFormSubmitting(true);
    try {
      const data: UpdateUserRequest = {
        status: formStatus,
        // FT30: send the actual value (incl. "") so an admin can CLEAR a
        // description. `|| undefined` dropped the empty string, and the backend's
        // drop-None filter then made clearing a silent no-op.
        description: formDescription,
      };
      await ftpApi.updateUser(token, editingUser.username, data);
      toast.success(`User "${editingUser.username}" updated`);
      closeDialogs();
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setFormSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!token || !deletingUser) return;
    setDeleteSubmitting(true);
    try {
      await ftpApi.deleteUser(token, deletingUser.username);
      toast.success(`User "${deletingUser.username}" deleted`);
      setDeletingUser(null);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setDeleteSubmitting(false);
    }
  }

  async function handleResetPassword() {
    if (!token || !resetPasswordUser) return;
    setResetSubmitting(true);
    try {
      const result = await ftpApi.resetPassword(
        token,
        resetPasswordUser.username,
      );
      setNewPassword(result.new_password);
      setResetPasswordUser(null);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  function formatDate(dateStr: string): string {
    if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "Never";
    return formatDateTime(dateStr);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">SFTP Users</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage SFTP user accounts, passwords, and quotas
          </p>
        </div>
        {canCreate && (
          <Button className="rounded-full" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Create User
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                Users ({pagination.totalCount})
              </CardTitle>
              <CardDescription>
                SFTP user accounts for your organisation
              </CardDescription>
            </div>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search users..."
              containerClassName="w-64"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="mb-4 h-12 w-12 text-muted-foreground/30" />
              <p className="text-muted-foreground">
                {searchTerm
                  ? "No users match your search"
                  : "No SFTP users yet"}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      direction={getSortDirection("username")}
                      onSort={() => requestSort("username")}
                    >
                      Username
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("status")}
                      onSort={() => requestSort("status")}
                    >
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("used_quota_size")}
                      onSort={() => requestSort("used_quota_size")}
                    >
                      Quota Used
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("quota_size")}
                      onSort={() => requestSort("quota_size")}
                    >
                      {/* FT29: every user's cap IS the org pool (shared home_dir),
                          not a per-user allocation — label it honestly. */}
                      Org Pool
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("last_login")}
                      onSort={() => requestSort("last_login")}
                    >
                      Last Login
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("created_at")}
                      onSort={() => requestSort("created_at")}
                    >
                      Created
                    </SortableTableHead>
                    <SortableTableHead disabled direction={null} onSort={() => {}} className="w-[50px]">
                      {null}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((user) => {
                    const quotaPercent =
                      user.quota_size > 0
                        ? (user.used_quota_size / user.quota_size) * 100
                        : 0;
                    return (
                      <TableRow key={user.username}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{user.username}</span>
                            {user.description && (
                              <p className="text-xs text-muted-foreground">
                                {user.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={user.status === 1 ? "default" : "secondary"}
                          >
                            {user.status === 1 ? "Active" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatBytes(user.used_quota_size)}</TableCell>
                        <TableCell>
                          <span
                            className={
                              quotaPercent > 90 ? "text-red-600 font-medium" : ""
                            }
                          >
                            {formatBytes(user.quota_size)}
                          </span>
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({quotaPercent.toFixed(0)}%)
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(user.last_login)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell>
                          {(canEdit || canDelete) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  data-testid={`user-actions-${user.username}`}
                                  aria-label={`Actions for ${user.username}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => openEditDialog(user)}
                                    >
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit User
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setResetPasswordUser(user)}
                                    >
                                      <KeyRound className="mr-2 h-4 w-4" />
                                      Reset Password
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setDeletingUser(user)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete User
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {pagination.totalCount > 0 && (
                <div className="mt-4 border-t pt-4">
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    pageSizeOptions={pageSizeOptions}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    canGoPrev={canGoPrev}
                    canGoNext={canGoNext}
                    onPageChange={goToPage}
                    onPageSizeChange={setPageSize}
                    onFirstPage={firstPage}
                    onLastPage={lastPage}
                    onPrevPage={prevPage}
                    onNextPage={nextPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create SFTP User</DialogTitle>
            <DialogDescription>
              Create a new SFTP user account for your organisation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-username">Username</Label>
                <Input
                  id="create-username"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  placeholder="e.g. john.doe"
                  required
                  minLength={3}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">
                  Password{" "}
                  <span className="text-muted-foreground font-normal">
                    (optional — auto-generated if blank)
                  </span>
                </Label>
                <Input
                  id="create-password"
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  placeholder="Leave blank to auto-generate"
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-description">Description</Label>
                <Input
                  id="create-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting || !formUsername}>
                {formSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={!!editingUser}
        onOpenChange={(open) => !open && closeDialogs()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User: {editingUser?.username}</DialogTitle>
            <DialogDescription>
              Update status or description for this user. Storage is shared
              across the organisation pool — there are no per-user quotas.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formStatus === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormStatus(1)}
                  >
                    Active
                  </Button>
                  <Button
                    type="button"
                    variant={formStatus === 0 ? "destructive" : "outline"}
                    size="sm"
                    onClick={() => setFormStatus(0)}
                  >
                    Disabled
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => closeDialogs()}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingUser}
        onOpenChange={(open) => !open && setDeletingUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SFTP User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingUser?.username}
              &quot;? This removes the SFTP user account. Their files remain in
              the organisation&rsquo;s shared storage &mdash; a re-created user
              would regain access to them. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation */}
      <AlertDialog
        open={!!resetPasswordUser}
        onOpenChange={(open) => !open && setResetPasswordUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password?</AlertDialogTitle>
            <AlertDialogDescription>
              Generate a new password for &quot;{resetPasswordUser?.username}
              &quot;? The current password will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              disabled={resetSubmitting}
            >
              {resetSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Password Display Dialog */}
      <Dialog
        open={!!newPassword}
        onOpenChange={(open) => !open && setNewPassword(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Password</DialogTitle>
            <DialogDescription>
              Copy this password now. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border bg-muted p-3">
            <code className="flex-1 break-all font-mono text-sm">
              {newPassword}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (newPassword) {
                  navigator.clipboard.writeText(newPassword);
                  toast.success("Password copied to clipboard");
                }
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewPassword(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
